/**
 * Przeciągnięcie karty rekordu kartoteki na tablicy Kanban = zwykły zapis rekordu, na tych samych
 * zasadach co formularz. Wspólny hak dla kartotek, których lista ma tablicę (kontrahenci, zadania);
 * kartoteka dostarcza tylko konfigurację (`RecordKanbanMoveConfig`): trasę zapisu, kod prawa edycji,
 * które rdzenne pola mają dedykowaną kolumnę, funkcję komunikatu błędu wspólną z formularzem i
 * ogólny komunikat zapasowy.
 *
 * Ta sama trasa co formularz (`PATCH` rekordu — walidacja, prawo edycji, zapis atomowy, audyt),
 * ta sama obsługa wyniku:
 *  - ostrzeżenie procedury kontrolnej → dialog „Zapisz mimo to" (`useControlWarning`, jak formularz);
 *    karta stoi w nowej kolumnie do decyzji, potwierdzenie ponawia zapis z `warningsAccepted`,
 *    anulowanie cofa kartę;
 *  - blokada procedury / błąd procedury → treść procedury (`useControlWarning`);
 *  - zadanie przycisku SQL na rekordzie → komunikat z opisem zadania;
 *  - pozostałe odrzucenia → komunikat wspólny z formularzem (`config.saveErrorMessage`), a gdy go
 *    brak — komunikat zapasowy (`config.fallbackToastKey`).
 * Każde odrzucenie kończy się odrzuconą obietnicą — tablica cofa wtedy kartę.
 *
 * Zapisywane jest pole, PO KTÓRYM TABLICA GRUPUJE (nie zawsze status). Przeciąganie działa tylko
 * dla pól, które przeciągnięcie umie zmienić: pola wyboru i tak/nie z katalogu (rdzenne i dodatkowe),
 * zapisywalne, z wartością w kolumnie równą wartości w bazie. Przy innym polu grupowania (firma,
 * kategoria — kolumna z nazwą zamiast identyfikatora, odwołania, pola tekstowe) tablica jest tylko
 * do podglądu. Bez prawa edycji kartoteki w aktywnej firmie — też tylko do podglądu (formularz w tej
 * sytuacji nie pokazuje „Edytuj"); zawężenia prawa per rekord rozstrzyga serwer.
 */

import { type ReactNode, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useToast } from '@/contexts/ToastContext';
import { useControlWarning } from '@/hooks/useControlWarning';
import { usePermission } from '@/hooks/usePermission';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import type { SystemModuleFieldDef } from '@/lib/ext-list-columns';
import { runConflictText } from '@/lib/record-procedures/run-texts';
import { extractRunConflict } from '@/lib/record-procedures/runs-api';
import { slugToCamel } from '@/lib/slug-to-camel';
import { resolveFieldLabel } from '@/lib/validation-toast';

/** Typy pól, których kolumna tablicy niesie dokładnie wartość zapisywaną w bazie. */
const MOVABLE_FIELD_TYPES = new Set(['select', 'boolean']);

/** Lista nie zna napisów przycisków z układu formularza — opis zadania bierze nazwę z katalogu. */
const NO_BUTTON_LABELS: ReadonlyMap<string, string> = new Map();

interface MovePayload {
  id: string;
  body: Record<string, unknown>;
}

/** Konfiguracja kartoteki — referencje powinny być stałe (poza renderem), inaczej odświeżają wywołania. */
export interface RecordKanbanMoveConfig {
  /** Trasa zapisu rekordu (`PATCH`), np. `(id) => '/contractors/' + id`. */
  savePath: (id: string) => string;
  /** Kod prawa edycji kartoteki (bramka przeciągania; rozstrzyga serwer per rekord). */
  editPermission: string;
  /**
   * Czy rdzenne pole o tym slugu ma na liście dedykowaną kolumnę (firma, kategoria, osoby…) —
   * kolumna niesie nazwę zamiast surowej wartości, więc po takim polu nie da się przeciągać.
   */
  hasDedicatedColumn: (slug: string) => boolean;
  /**
   * Komunikat odrzuconego zapisu wspólny z formularzem kartoteki; `null` = błąd spoza jego listy,
   * wtedy toast pokazuje `fallbackToastKey`.
   */
  saveErrorMessage: (
    err: unknown,
    t: TFunction,
    fieldLabel: (field: string) => string | null,
  ) => string | null;
  /** Klucz i18n ogólnego komunikatu „nie udało się zapisać". */
  fallbackToastKey: string;
}

/**
 * Pole katalogu, które przeciągnięcie umie zapisać — `null`, gdy tablica ma być tylko do podglądu.
 * `hasDedicatedColumn` — patrz `RecordKanbanMoveConfig`.
 */
export function resolveMovableField(
  fields: readonly SystemModuleFieldDef[],
  fieldId: string,
  hasDedicatedColumn: (slug: string) => boolean,
): SystemModuleFieldDef | null {
  const field = fields.find((f) => f.slug === fieldId);
  if (!field || field.readOnly) return null;
  if (!MOVABLE_FIELD_TYPES.has(field.fieldType)) return null;
  // Rdzenne pola z dedykowaną kolumną nie mają na liście kolumny z surową wartością — nie da się
  // z kolumny tablicy wyczytać, co zapisać.
  if (field.isCore && hasDedicatedColumn(field.slug)) return null;
  return field;
}

/** Ciało zapisu dla upuszczenia karty w kolumnie `value` (null = „Nieprzypisane"). */
export function buildMoveBody(
  field: SystemModuleFieldDef,
  value: string | null,
): Record<string, unknown> {
  const parsed = field.fieldType === 'boolean' && value !== null ? value === 'true' : value;
  return field.isCore
    ? { [slugToCamel(field.slug)]: parsed }
    : { extValues: { [field.slug]: parsed } };
}

export interface RecordKanbanMove {
  /** Callback tablicy (`ConfigurableTable.onRowUpdate`); odrzucenie = tablica cofa kartę. */
  onRowUpdate: (rowId: string, fieldId: string, newValue: string | null) => Promise<void>;
  /** Czy kartę wolno przeciągać przy grupowaniu po danym polu. */
  canMoveField: (fieldId: string) => boolean;
  /** Dialog ostrzeżenia procedury kontrolnej — osadź w JSX strony. */
  dialog: ReactNode;
}

export function useRecordKanbanMove(
  config: RecordKanbanMoveConfig,
  opts: {
    fields: readonly SystemModuleFieldDef[];
    /** Po udanym zapisie (odświeżenie listy). */
    onSaved: () => void;
  },
): RecordKanbanMove {
  const { fields, onSaved } = opts;
  const { savePath, editPermission, hasDedicatedColumn, saveErrorMessage, fallbackToastKey } =
    config;
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { allowed } = usePermission(editPermission);

  // Upuszczenie czekające na decyzję w dialogu ostrzeżenia — obietnica, na którą czeka tablica.
  const pendingRef = useRef<{ resolve: () => void; reject: (err: unknown) => void } | null>(null);
  const saveRef = useRef<(payload: MovePayload & { warningsAccepted?: boolean }) => Promise<void>>(
    async () => undefined,
  );

  const settlePending = (outcome: Promise<void> | Error) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;
    if (outcome instanceof Error) pending.reject(outcome);
    else outcome.then(pending.resolve, pending.reject);
  };

  const controlWarning = useControlWarning<MovePayload>({
    mutate: (payload) => settlePending(saveRef.current(payload)),
    onCancel: () => settlePending(new Error('CONTROL_WARNING_CANCELLED')),
  });

  const fieldLabel = (field: string) => resolveFieldLabel(fields, field);

  saveRef.current = async ({ id, body, warningsAccepted }) => {
    try {
      await api.patch(savePath(id), warningsAccepted ? { ...body, warningsAccepted } : body);
    } catch (err) {
      if (extractErrorCode(err) === 'CONTROL_WARNING') {
        // Karta zostaje w nowej kolumnie, dopóki użytkownik nie zdecyduje w dialogu.
        return new Promise<void>((resolve, reject) => {
          pendingRef.current = { resolve, reject };
          controlWarning.handleError(err, { id, body });
        });
      }
      if (!controlWarning.handleError(err, { id, body })) {
        const conflict = extractRunConflict(err);
        const title =
          conflict?.code === 'RECORD_LOCKED_BY_RUN'
            ? runConflictText(conflict, NO_BUTTON_LABELS, t, i18n.language)
            : (saveErrorMessage(err, t, fieldLabel) ?? t(fallbackToastKey));
        toast.show({ variant: 'error', title });
      }
      throw err;
    }
    onSaved();
  };

  const canMoveField = useCallback(
    (fieldId: string) =>
      allowed && resolveMovableField(fields, fieldId, hasDedicatedColumn) !== null,
    [allowed, fields, hasDedicatedColumn],
  );

  const onRowUpdate = useCallback(
    async (rowId: string, fieldId: string, newValue: string | null) => {
      const field = resolveMovableField(fields, fieldId, hasDedicatedColumn);
      // Tablica nie pozwala przeciągnąć przy takim polu (`canMoveField`) — straż na wszelki wypadek.
      if (!field) throw new Error('KANBAN_FIELD_NOT_MOVABLE');
      await saveRef.current({ id: rowId, body: buildMoveBody(field, newValue) });
    },
    [fields, hasDedicatedColumn],
  );

  return { onRowUpdate, canMoveField, dialog: controlWarning.dialog };
}
